import { useBunja } from 'bunja/react';
import { useAtom } from 'jotai';
import { useMount } from 'react-use';

import { CurrencyWarning } from '@flipster/apis/v1/currency/types';
import { FormattedMessage } from '@flipster/i18n';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { stack } from '@flipster/styled-system/patterns';
import { getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import segment, { WalletEvent } from '@flipster/utils/segment';

import { useAuth } from '../../../auth/state';
import { getCurrencyWarningStorageKey } from '../storage-key';
import { warningAlertBunja } from './state';

interface CurrencyWarningAlertProps {
  warningData: CurrencyWarning;
  onClose?: () => void;
}

export const CurrencyWarningAlert = ({
  warningData: { currency, blockchainName: network = 'INTERNAL', title, body },
  onClose,
}: CurrencyWarningAlertProps) => {
  const { user } = useAuth();
  const { hasSeenWarningAlertKeysAtom } = useBunja(warningAlertBunja);
  const [hasSeenWarningAlertKeys, setHasSeenWarningAlertKeys] = useAtom(hasSeenWarningAlertKeysAtom);

  useMount(() => {
    if (!user?.userId) return;
    let isChecked = false;

    const storageKey = getCurrencyWarningStorageKey({
      userId: String(user.userId),
      warningData: { currency, blockchainName: network, title, body },
    });

    if (getLocalStorage(storageKey) || hasSeenWarningAlertKeys.includes(storageKey)) return;
    setHasSeenWarningAlertKeys((prev) => [...prev, storageKey]);

    dialog.open({
      title: {
        leftAddon: <InformationCircleIcon color='#faad14' />,
        label: title ?? 'Notice',
      },
      content: (
        <div className={stack()}>
          {body}
          <Checkbox
            css={{ color: 'content.default.level-2', mt: 18 }}
            onChange={(e) => {
              isChecked = e.target.checked;
              if (isChecked) segment.track(WalletEvent.DepositWarningHideClick, { currency, network });
            }}
          >
            <FormattedMessage id='Deposit.r250401.Dont_show_warning_again' />
          </Checkbox>
        </div>
      ),
      buttons: {
        primary: (
          <Dialog.Button
            hierarchy='primary'
            label={<FormattedMessage id='Common.r250401.Close' />}
            onClick={() => {
              if (isChecked) setLocalStorage(storageKey, true);
              onClose?.();
            }}
          />
        ),
      },
    });
  });
  return null;
};
